import { Button } from '@parentsquare/squarekit/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@parentsquare/squarekit/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@parentsquare/squarekit/components/ui/dialog';
import { Drawer } from '@parentsquare/squarekit/components/ui/drawer';
import { ScrollArea } from '@parentsquare/squarekit/components/ui/scroll-area';
import { Separator } from '@parentsquare/squarekit/components/ui/separator';
import { ChatMessage as SquareKitChatMessage } from '@parentsquare/squarekit/molecules/chat/ChatMessage';
import { ArrowDown, Minus, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    BOT_NAME,
    DEFAULT_AVATAR_TYPE,
    DEFAULT_PRIMARY_COLOR,
    DEFAULT_TEXT_COLOR,
    getAvatarInitials,
    type AvatarType,
} from '@/Public/ChatBot/Constants';
import { useChatHistory } from '@/Public/ChatBot/Hooks/useChatHistory';
import { useChatScroll } from '@/Public/ChatBot/Hooks/useChatScroll';
import { useChatbotApi } from '@/Public/ChatBot/Hooks/useChatbotApi';
import type { HistoryEntry } from '@/Public/ChatBot/Types';
import { useThemeColors } from '@/Hooks/useThemeColors';
import { ChatBotThemeProvider } from '@/Public/ChatBot/Hooks/useChatBotTheme';
import { shouldShowDateSeparator, formatDateSeparator } from '@/Public/ChatBot/Utils/DateUtils';
import { renderAvatar } from './BotAvatar';
import { ChatBubbleSparkleIcon } from './ChatBubbleSparkleIcon';
import { ChatInput } from './ChatInput';
import { ChatMessage } from './ChatMessage';
import { FeedbackDialog } from './FeedbackDialog';
import { TypingIndicator } from './TypingIndicator';

interface ChatContainerProps {
    primaryColor?: string;
    textColor?: string;
    siteName: string;
    assistantName?: string;
    avatarType?: AvatarType;
    avatarImageUrl?: string;
    previewMode?: boolean;
}

export const ChatContainer = ({
    primaryColor = DEFAULT_PRIMARY_COLOR,
    textColor = DEFAULT_TEXT_COLOR,
    siteName,
    assistantName = BOT_NAME,
    avatarType = DEFAULT_AVATAR_TYPE,
    avatarImageUrl,
    previewMode = false,
}: ChatContainerProps): React.ReactNode => {
    const initials = getAvatarInitials(assistantName);

    const [cardElement, setCardElement] = useState<HTMLDivElement | null>(null);
    const cardRef = useCallback((node: HTMLDivElement | null) => setCardElement(node), []);

    useThemeColors({
        primaryColor,
        textColor,
        element: previewMode ? cardElement : undefined,
    });

    const { messages, addMessage, setFeedback, clearHistory } = useChatHistory();
    const { sendMessage, sendFeedback, isLoading } = useChatbotApi();

    const [isOpen, setIsOpen] = useState(false);
    const [isWelcomeLoading, setIsWelcomeLoading] = useState(false);
    const [feedbackDialog, setFeedbackDialog] = useState<{ messageId: string; direction: 'up' | 'down' } | null>(null);

    const {
        scrollAreaRef,
        lastMessageRef,
        typingRef,
        bottomSentinelRef,
        showScrollButton,
        scrollToBottom,
        resetScroll,
    } = useChatScroll(messages, isLoading, isOpen);

    const feedbackSubmittedRef = useRef(false);
    const feedbackTriggerRef = useRef<HTMLElement | null>(null);
    const lastInputWasKeyboardRef = useRef(false);
    const chatIdMapRef = useRef<Map<string, string>>(new Map());
    const sessionIdRef = useRef<string | null>(null);
    const launcherRef = useRef<HTMLButtonElement>(null);
    const endChatTriggerRef = useRef<HTMLButtonElement>(null);
    const shouldFocusLauncherRef = useRef(false);

    useEffect(() => {
        const markKeyboard = (): void => {
            lastInputWasKeyboardRef.current = true;
        };
        const markPointer = (): void => {
            lastInputWasKeyboardRef.current = false;
        };
        document.addEventListener('keydown', markKeyboard, true);
        document.addEventListener('pointerdown', markPointer, true);
        return () => {
            document.removeEventListener('keydown', markKeyboard, true);
            document.removeEventListener('pointerdown', markPointer, true);
        };
    }, []);

    const submitPendingFeedback = (text = ''): void => {
        if (feedbackDialog && !feedbackSubmittedRef.current) {
            feedbackSubmittedRef.current = true;
            const serverChatId = chatIdMapRef.current.get(feedbackDialog.messageId) ?? '';
            sendFeedback(serverChatId, feedbackDialog.direction, text);
        }
    };

    const handleCloseAutoFocus = (e: Event): void => {
        e.preventDefault();
        const trigger = feedbackTriggerRef.current;
        feedbackTriggerRef.current = null;
        if (lastInputWasKeyboardRef.current && trigger && document.contains(trigger)) {
            trigger.focus();
        } else {
            cardElement?.focus();
        }
    };

    const handleRequestFeedback = (messageId: string, direction: 'up' | 'down'): void => {
        feedbackTriggerRef.current = document.activeElement as HTMLElement | null;
        feedbackSubmittedRef.current = false;
        setFeedback(messageId, direction);
        setFeedbackDialog({ messageId, direction });
    };

    const handleFeedbackSubmit = (text: string): void => {
        submitPendingFeedback(text);
        setFeedbackDialog(null);
    };

    const handleOpen = (): void => {
        if (messages.length === 0) {
            setIsWelcomeLoading(true);
            setTimeout(() => setIsWelcomeLoading(false), 500);
        }
        setIsOpen(true);
    };

    const handleMinimize = (): void => {
        submitPendingFeedback();
        setFeedbackDialog(null);
        feedbackTriggerRef.current = null;
        shouldFocusLauncherRef.current = true;
        setIsOpen(false);
    };

    const [showEndChatDialog, setShowEndChatDialog] = useState(false);

    const handleRequestClose = (): void => {
        setShowEndChatDialog(true);
    };

    const handleConfirmEndChat = (): void => {
        setShowEndChatDialog(false);
        handleClearHistory();
        shouldFocusLauncherRef.current = true;
        setIsOpen(false);
    };

    const handleCancelEndChat = (): void => {
        setShowEndChatDialog(false);
        requestAnimationFrame(() => {
            endChatTriggerRef.current?.focus();
        });
    };

    const handleDrawerOpenChange = (open: boolean): void => {
        if (!open) {
            submitPendingFeedback();
            setFeedbackDialog(null);
        }
    };

    useEffect(() => {
        window.parent?.postMessage({ type: 'chatbot-resize', open: isOpen }, window.location.origin);

        if (!isOpen) {
            document.body.style.pointerEvents = '';
            document.body.style.overflow = '';
            document.body.removeAttribute('data-scroll-locked');
            return;
        }

        // iPhone: Radix Dialog/Drawer modal mode applies pointer-events:none,
        // overflow:hidden, and data-scroll-locked to <body>, which makes the
        // page unresponsive inside an iframe on iOS. This observer immediately
        // undoes those styles while keeping the overlay and click-to-dismiss.
        const cleanBodyStyles = (): void => {
            if (document.body.style.pointerEvents === 'none') {
                document.body.style.pointerEvents = '';
            }
            if (document.body.style.overflow === 'hidden') {
                document.body.style.overflow = '';
            }
            if (document.body.hasAttribute('data-scroll-locked')) {
                document.body.removeAttribute('data-scroll-locked');
            }
        };

        const observer = new MutationObserver(cleanBodyStyles);
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['style', 'data-scroll-locked'],
        });

        return () => observer.disconnect();
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            requestAnimationFrame(() => {
                endChatTriggerRef.current?.focus();
            });
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen && shouldFocusLauncherRef.current) {
            shouldFocusLauncherRef.current = false;
            requestAnimationFrame(() => {
                launcherRef.current?.focus();
            });
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            if (showEndChatDialog || feedbackDialog) return;

            const target = e.target as HTMLElement;
            if (target?.closest('[role="dialog"]')) return;

            shouldFocusLauncherRef.current = true;
            setIsOpen(false);
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, showEndChatDialog, feedbackDialog]);

    const [flashMessage, setFlashMessage] = useState<string | null>(null);
    const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleSendMessage = async (content: string): Promise<void> => {
        const history: HistoryEntry[] = messages
            .filter((msg): msg is typeof msg & { role: 'user' | 'assistant' } => msg.role === 'user' || msg.role === 'assistant')
            .slice(-20)
            .map(({ role, content: text }) => ({ role, content: text }));

        addMessage({ role: 'user', content });

        const sessionId = sessionIdRef.current ?? crypto.randomUUID();
        sessionIdRef.current = sessionId;

        try {
            const { chatId, response } = await sendMessage(content, sessionId, history);
            const assistantContent =
                response.content ||
                (response.answerable
                    ? 'Sorry, I encountered an error. Please try again.'
                    : "I'm sorry, I don't have enough information to answer that question.");

            const messageId = addMessage({
                role: 'assistant',
                content: assistantContent,
                citations: response.citations,
                answerable: response.answerable,
            });

            chatIdMapRef.current.set(messageId, chatId);
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') return;
            const message =
                error instanceof Error
                    ? error.message
                    : "I'm having trouble reaching the server right now. Please try again in a moment.";
            addMessage({
                role: 'assistant',
                content: message,
            });
        }
    };

    const handleClearHistory = (): void => {
        clearHistory();
        chatIdMapRef.current.clear();
        sessionIdRef.current = null;
        resetScroll();
        setFlashMessage('Chat history cleared');
        if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
        flashTimeoutRef.current = setTimeout(() => setFlashMessage(null), 3000);
    };

    const isFeedbackOpen = !!feedbackDialog;

    useEffect(() => {
        return () => {
            if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
        };
    }, []);

    if (!isOpen && !previewMode) {
        return (
            <button
                ref={launcherRef}
                onClick={handleOpen}
                className="tw:relative tw:flex tw:h-14 tw:w-14 tw:shrink-0 tw:cursor-pointer tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-full tw:border-none tw:p-0 tw:shadow-[0px_4px_12px_0px_rgba(0,0,0,0.15),0px_2px_4px_0px_rgba(0,0,0,0.1),0px_0px_0px_3px_rgba(255,255,255,0.3)]"
                style={{ backgroundColor: primaryColor }}
                aria-label="Open chat"
            >
                <ChatBubbleSparkleIcon
                    className="tw:h-[38px] tw:w-[38px] tw:drop-shadow-[0px_1px_2px_rgba(0,0,0,0.2)]"
                    style={{ color: textColor }}
                />
                <span className="tw:pointer-events-none tw:absolute tw:inset-0 tw:rounded-full tw:shadow-[inset_0px_0px_0px_1px_rgba(0,0,0,0.1)]" />
            </button>
        );
    }

    const welcomeMessage = `Hi! I'm the AI assistant for the ${siteName || '[District or School Name]'} website. How can I help you today?`;

    const chatCard = (
        <Card
            ref={cardRef}
            tabIndex={previewMode ? undefined : -1}
            className={`tw:relative tw:flex tw:max-w-[400px] tw:w-full tw:flex-col tw:overflow-clip tw:p-0 tw:focus:outline-none ${previewMode ? 'tw:h-[500px]' : 'tw:h-[700px] tw:max-h-[90vh]'}`}
        >
            <CardHeader className="tw:m-0 tw:flex tw:flex-row tw:items-center tw:gap-4 tw:rounded-none tw:bg-surface-container tw:py-3 tw:pl-4 tw:pr-2">
                {renderAvatar(avatarType, initials, avatarImageUrl, assistantName, 'md')}
                <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
                    <span className="tw:text-sm tw:font-semibold tw:text-interaction-textLink">{assistantName}</span>
                    <span className="tw:text-xs tw:text-type-secondary">
                        {`Chats may be reviewed by ${siteName || '[District or School Name]'} and ParentSquare.`}
                    </span>
                </div>
                <div className="tw:flex tw:items-center">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={previewMode ? undefined : handleMinimize}
                        disabled={previewMode}
                        aria-label="Minimize chat"
                    >
                        <Minus className="tw:h-4 tw:w-4" />
                    </Button>
                    <Button
                        ref={previewMode ? undefined : endChatTriggerRef}
                        variant="ghost"
                        size="icon"
                        onClick={previewMode ? undefined : handleRequestClose}
                        disabled={previewMode}
                        aria-label="End chat"
                        aria-haspopup={previewMode ? undefined : 'dialog'}
                        aria-expanded={previewMode ? undefined : showEndChatDialog}
                        aria-controls={previewMode ? undefined : 'end-chat-dialog'}
                    >
                        <X className="tw:h-4 tw:w-4" />
                    </Button>
                </div>
            </CardHeader>

            <Separator />

            {!previewMode && flashMessage && (
                <div className="tw:bg-surface-container tw:px-3 tw:py-1.5 tw:text-center tw:text-xs tw:text-type-secondary">
                    {flashMessage}
                </div>
            )}

            <CardContent className="tw:relative tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-hidden tw:p-0">
                <ScrollArea
                    ref={previewMode ? undefined : scrollAreaRef}
                    className="tw:h-full tw:w-full tw:flex-1"
                >
                    <div
                        className="tw:w-full tw:px-6 tw:py-4"
                        role={previewMode ? undefined : 'log'}
                        aria-live={previewMode ? undefined : 'polite'}
                        aria-atomic={previewMode ? undefined : 'false'}
                    >
                        {previewMode ? (
                            <SquareKitChatMessage
                                authorName={assistantName}
                                timestamp=""
                                body={welcomeMessage}
                                isCurrentUser={false}
                                avatar={renderAvatar(avatarType, initials, avatarImageUrl, assistantName, 'sm')}
                            />
                        ) : messages.length === 0 ? (
                            isWelcomeLoading ? (
                                <TypingIndicator />
                            ) : (
                                <SquareKitChatMessage
                                    authorName={assistantName}
                                    timestamp=""
                                    body={welcomeMessage}
                                    isCurrentUser={false}
                                    avatar={renderAvatar(avatarType, initials, avatarImageUrl, assistantName, 'sm')}
                                />
                            )
                        ) : (
                            <div className="tw:space-y-4">
                                {messages.map((message, index) => (
                                    <React.Fragment key={message.id}>
                                        {shouldShowDateSeparator(message, messages[index - 1]) && (
                                            <Separator
                                                version="v1"
                                                showLabel
                                                label={formatDateSeparator(message.timestamp)}
                                            />
                                        )}
                                        <ChatMessage
                                            ref={index === messages.length - 1 ? lastMessageRef : undefined}
                                            message={message}
                                            onRequestFeedback={handleRequestFeedback}
                                            hideActions={isFeedbackOpen}
                                            isFeedbackOpen={isFeedbackOpen}
                                        />
                                    </React.Fragment>
                                ))}
                                {isLoading && (
                                    <div ref={typingRef}>
                                        <TypingIndicator />
                                    </div>
                                )}
                                <div
                                    ref={bottomSentinelRef}
                                    aria-hidden="true"
                                />
                            </div>
                        )}
                    </div>
                </ScrollArea>

                {!previewMode && showScrollButton && (
                    <Button
                        variant="secondary"
                        size="icon"
                        onClick={scrollToBottom}
                        aria-label="Scroll to bottom"
                        className="tw:absolute tw:bottom-2 tw:left-1/2 tw:z-10 tw:h-7 tw:w-7 tw:-translate-x-1/2 tw:rounded-full tw:shadow-md"
                    >
                        <ArrowDown className="tw:h-3.5 tw:w-3.5" />
                    </Button>
                )}
            </CardContent>

            {!previewMode && (
                <>
                    <Separator />

                    <CardFooter className="tw:m-0 tw:flex tw:flex-col tw:gap-2 tw:rounded-none tw:bg-surface-container tw:px-3 tw:pb-4 tw:pt-3">
                        <ChatInput
                            onSendMessage={handleSendMessage}
                            isLoading={isLoading}
                        />
                        <p className="tw:pb-1.5 tw:text-center tw:text-[9px] tw:text-type-secondary">
                            AI can make mistakes. Please verify responses. Don&apos;t share personal details like names,
                            phone numbers, and email addresses.
                        </p>
                    </CardFooter>
                </>
            )}

            {!previewMode && (
                <>
                    <Drawer
                        open={!!feedbackDialog}
                        onOpenChange={handleDrawerOpenChange}
                        shouldScaleBackground={false}
                        noBodyStyles
                        contained
                        container={cardElement}
                    >
                        <FeedbackDialog
                            open={!!feedbackDialog}
                            onSubmit={handleFeedbackSubmit}
                            onCloseAutoFocus={handleCloseAutoFocus}
                        />
                    </Drawer>

                    <Dialog
                        open={showEndChatDialog}
                        onOpenChange={(open) => {
                            if (!open) handleCancelEndChat();
                        }}
                    >
                        <DialogContent
                            id="end-chat-dialog"
                            contained
                            portalContainer={cardElement}
                            onClose={handleCancelEndChat}
                        >
                            <DialogHeader className="tw:text-left">
                                <DialogTitle>End Chat?</DialogTitle>
                            </DialogHeader>
                            <DialogDescription>Are you sure you want to end this chat now?</DialogDescription>
                            <DialogFooter className="tw:flex-row tw:justify-end tw:gap-2">
                                <Button
                                    variant="secondary"
                                    onClick={handleCancelEndChat}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleConfirmEndChat}
                                >
                                    End Chat
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </>
            )}
        </Card>
    );

    return (
        <ChatBotThemeProvider value={{ primaryColor, textColor, assistantName, avatarType, avatarImageUrl }}>
            {chatCard}
        </ChatBotThemeProvider>
    );
};
